import { useState } from "react";
import Alert from "@mui/material/Alert";
import BlockIcon from "@mui/icons-material/Block";
import { useQueryClient } from "@tanstack/react-query";
import { cancel } from "../../services/antecipebackoffice/advanceRequest";
import { useNotify } from "../../shared/ui/feedback/SnackbarProvider";
import ReasonDialog from "./ReasonDialog";
import ReasonPicker from "./ReasonPicker";
import { useReasonCapture } from "./useReasonCapture";
import { captureErrorMessage } from "./errors";
import { reasonsKey } from "./useReasons";

export type CancelPackageDialogProps = {
  row: { id: number };
  context?: string;
  onClose: () => void;
  onCanceled: () => void;
};

/** Janela (a): cancelar pacote com motivo obrigatório (CA-F1a-01/02). Monte só enquanto aberta. */
export default function CancelPackageDialog({ row, context, onClose, onCanceled }: CancelPackageDialogProps) {
  const capture = useReasonCapture("cancellation");
  const notify = useNotify();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    if (!capture.valid) return;
    setBusy(true);
    setError(null);
    try {
      await cancel(row.id, capture.toCapture());
      setBusy(false);
      notify({ severity: "success", message: "Pacote cancelado." });
      onCanceled();
    } catch (e) {
      setBusy(false);
      if ((e as { response?: { data?: { code?: string } } })?.response?.data?.code === "REASON_INVALID") {
        void qc.invalidateQueries({ queryKey: reasonsKey("cancellation") });
      }
      setError(captureErrorMessage(e, "Não foi possível cancelar o pacote."));
    }
  };

  return (
    <ReasonDialog
      title="Cancelar pacote"
      context={context}
      size="sm"
      destructive
      confirmLabel="Cancelar pacote"
      confirmIcon={<BlockIcon />}
      busy={busy}
      error={error}
      valid={capture.valid}
      onConfirm={confirm}
      onClose={onClose}
    >
      <Alert severity="warning">
        <b>Esta ação não pode ser desfeita.</b> O pacote vai para “Cancelada” e o médico precisa abrir um novo pedido para antecipar estes plantões.
      </Alert>
      <ReasonPicker capture={capture} />
    </ReasonDialog>
  );
}
